import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Input } from '../../BasicComponents/Input/Input';
import { Button } from '../../BasicComponents/Button/Button';
import { Spinner } from '../../BasicComponents/Spinner/Spinner';
import { formatMoneyParts } from '../../../utils/currency';
import type { CustomerLookupProps, CustomerLookupResult } from './types';

// ─── CustomerLookup ───────────────────────────────────────────────────────────

const MIN_QUERY_LEN = 3;
const DEBOUNCE_MS = 300;

export function CustomerLookup({
  onSelect,
  onClear,
  selectedCustomer,
  isLoading = false,
  onSearch,
  currency = 'INR',
  className,
}: CustomerLookupProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CustomerLookupResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Drop stale responses if the user keeps typing.
  const reqIdRef = useRef(0);

  const runSearch = useCallback(
    async (q: string) => {
      if (!onSearch) return;
      const trimmed = q.trim();
      if (trimmed.length < MIN_QUERY_LEN) {
        setResults([]);
        setSearched(false);
        setOpen(false);
        return;
      }
      const id = ++reqIdRef.current;
      setSearching(true);
      try {
        const list = await onSearch(trimmed);
        if (id !== reqIdRef.current) return;
        setResults(list);
        setSearched(true);
        setOpen(true);
      } catch {
        if (id !== reqIdRef.current) return;
        setResults([]);
        setSearched(true);
        setOpen(true);
      } finally {
        if (id === reqIdRef.current) setSearching(false);
      }
    },
    [onSearch],
  );

  // Debounced search as the cashier types.
  useEffect(() => {
    if (!onSearch) return;
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LEN) {
      reqIdRef.current++;
      setResults([]);
      setSearched(false);
      setOpen(false);
      setSearching(false);
      return;
    }
    const t = setTimeout(() => {
      void runSearch(trimmed);
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query, onSearch, runSearch]);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const handleSelect = useCallback(
    (c: CustomerLookupResult) => {
      onSelect(c);
      setQuery('');
      setResults([]);
      setOpen(false);
      setSearched(false);
    },
    [onSelect],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        void runSearch(query);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    },
    [query, runSearch],
  );

  // If a customer is already selected, show compact card
  if (selectedCustomer) {
    return (
      <div
        className={[
          'flex items-center gap-3 rounded-lg border border-success-200 bg-success-50 dark:bg-success-900/20 dark:border-success-700 px-3 py-2',
          className ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {/* Avatar */}
        <div className="h-8 w-8 rounded-full bg-brand-100 dark:bg-brand-900 flex items-center justify-center shrink-0">
          <span className="text-sm font-bold text-brand-700 dark:text-brand-300">
            {selectedCustomer.name.charAt(0).toUpperCase()}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-100 truncate">
            {selectedCustomer.name}
          </p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">{selectedCustomer.phone}</p>
          {(selectedCustomer.loyaltyPoints !== undefined || selectedCustomer.creditBalance !== undefined) && (
            <p className="text-xs text-brand-600 dark:text-brand-400 mt-0.5">
              {selectedCustomer.loyaltyPoints !== undefined && `${selectedCustomer.loyaltyPoints} pts`}
              {selectedCustomer.loyaltyPoints !== undefined && selectedCustomer.creditBalance !== undefined && ' · '}
              {selectedCustomer.creditBalance !== undefined &&
                `${formatMoneyParts(selectedCustomer.creditBalance, currency, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                  useGrouping: false,
                })} credit`}
            </p>
          )}
        </div>
        {onClear && (
          <button
            type="button"
            onClick={onClear}
            aria-label="Remove customer"
            className="p-1 rounded text-neutral-400 hover:text-danger-500 dark:hover:text-danger-400 transition-colors shrink-0"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>
    );
  }

  const showSpinner = isLoading || searching;
  const showDropdown = open && searched && query.trim().length >= MIN_QUERY_LEN;

  return (
    <div ref={containerRef} className={`relative ${className ?? ''}`}>
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <Input
            ref={inputRef}
            value={query}
            onChange={setQuery}
            onKeyDown={handleKeyDown}
            placeholder="Name, phone or ID"
            disabled={isLoading}
            aria-label="Search customer"
          />
        </div>
        {showSpinner ? (
          <Spinner size="sm" />
        ) : (
          <Button
            variant="secondary"
            size="md"
            disabled={!query.trim()}
            aria-label="Search customer"
            onClick={() => void runSearch(query)}
          >
            Search
          </Button>
        )}
      </div>

      {showDropdown && (
        <div
          role="listbox"
          aria-label="Customer search results"
          className="absolute left-0 right-0 top-full mt-1 z-dropdown max-h-72 overflow-y-auto rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-lg"
        >
          {results.length === 0 ? (
            <div className="px-3 py-3 text-sm text-neutral-500 dark:text-neutral-400">
              No customers found
            </div>
          ) : (
            results.map((c) => (
              <button
                key={c.id}
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => handleSelect(c)}
                className="w-full text-left px-3 py-2 hover:bg-brand-50 dark:hover:bg-brand-900/20 border-b border-neutral-100 dark:border-neutral-700 last:border-b-0 transition-colors"
              >
                <p className="text-sm font-semibold text-neutral-800 dark:text-neutral-100 truncate">
                  {c.name}
                </p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  {c.phone}
                  {c.loyaltyPoints !== undefined && ` · ${c.loyaltyPoints} pts`}
                </p>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
